import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut, Users, Mail, Send, Download, Trash2, Plus, GraduationCap, Loader2, RefreshCw,
  KanbanSquare, Workflow, Zap, X, StickyNote,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "tlc_admin_token";

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });

const TEMPLATE_CATEGORIES = [
  "Confirmation & Reminders",
  "Class Follow-up",
  "Pipeline / Stage",
  "Nurture",
  "Re-engagement",
  "General",
];

// ---------- Login ----------
const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      onLogin();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-6">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="bg-white rounded-[2rem] p-9 w-full max-w-md shadow-[0_18px_50px_rgb(0,0,0,0.10)] border border-[#E7E5E4]"
      >
        <div className="flex items-center gap-2.5 mb-6">
          <span className="w-10 h-10 rounded-xl bg-[#D97757] flex items-center justify-center">
            <GraduationCap className="w-5 h-5 text-white" />
          </span>
          <span className="font-display font-semibold text-[#1C1917] text-lg">Admin · The Lending Classroom</span>
        </div>
        <Label className="text-sm font-medium text-[#1C1917]">Email</Label>
        <Input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          className="mt-1.5 mb-4 rounded-xl border-[#E7E5E4] focus-visible:ring-[#D97757] h-11" />
        <Label className="text-sm font-medium text-[#1C1917]">Password</Label>
        <Input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
          className="mt-1.5 mb-6 rounded-xl border-[#E7E5E4] focus-visible:ring-[#D97757] h-11" />
        <Button data-testid="admin-login-btn" type="submit" disabled={loading}
          className="w-full h-12 rounded-xl bg-[#D97757] hover:bg-[#C26243] text-white font-semibold">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign in"}
        </Button>
      </form>
    </div>
  );
};

// ---------- Contacts ----------
const Contacts = ({ classes }) => {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/contacts`, { headers: authHeader(), params: { class_id: filter } });
      setRows(res.data);
    } catch { toast.error("Failed to load contacts"); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const url = `${API}/admin/contacts/export?class_id=${filter}`;
    fetch(url, { headers: authHeader() })
      .then((r) => r.blob())
      .then((b) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = "contacts.csv";
        a.click();
      });
  };

  return (
    <div data-testid="admin-contacts">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="contacts-filter" className="w-64 rounded-xl border-[#E7E5E4] h-10">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All classes</SelectItem>
            {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={load} variant="outline" className="rounded-xl h-10 gap-2" data-testid="contacts-refresh">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
        <Button onClick={exportCsv} className="rounded-xl h-10 gap-2 bg-[#1C1917] hover:bg-black text-white" data-testid="contacts-export">
          <Download className="w-4 h-4" /> Export CSV
        </Button>
        <span className="text-sm text-[#57534E] ml-auto">{rows.length} contacts</span>
      </div>

      <div className="bg-white rounded-2xl border border-[#E7E5E4] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F4EBE1] text-[#1C1917]">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Name</th>
              <th className="text-left px-4 py-3 font-semibold">Email</th>
              <th className="text-left px-4 py-3 font-semibold">Phone</th>
              <th className="text-left px-4 py-3 font-semibold">Role</th>
              <th className="text-left px-4 py-3 font-semibold">Referred by</th>
              <th className="text-left px-4 py-3 font-semibold">Class</th>
              <th className="text-left px-4 py-3 font-semibold">Zoho</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="px-4 py-8 text-center text-[#57534E]">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan="7" className="px-4 py-8 text-center text-[#57534E]">No contacts yet.</td></tr>
            ) : rows.map((r, i) => (
              <tr key={i} className="border-t border-[#E7E5E4]">
                <td className="px-4 py-3 font-medium text-[#1C1917]">{r.first_name} {r.last_name}</td>
                <td className="px-4 py-3 text-[#57534E]">{r.email}</td>
                <td className="px-4 py-3 text-[#57534E]">{r.phone || "—"}</td>
                <td className="px-4 py-3 text-[#57534E]">{r.role || "—"}</td>
                <td className="px-4 py-3 text-[#57534E]">{r.referred_by || "—"}</td>
                <td className="px-4 py-3 text-[#57534E]">{r.class_label || r.class_id}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${r.zoho_synced ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                    {r.zoho_synced ? "Synced" : "Pending"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------- Templates ----------
const Templates = ({ classes }) => {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ name: "", subject: "", html_body: "", class_id: "", category: "General" });
  const [catFilter, setCatFilter] = useState("all");

  const load = async () => {
    try { setList((await axios.get(`${API}/admin/templates`, { headers: authHeader() })).data); }
    catch { toast.error("Failed to load templates"); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name || !form.subject || !form.html_body) { toast.error("Fill name, subject and body"); return; }
    try {
      await axios.post(`${API}/admin/templates`, form, { headers: authHeader() });
      toast.success("Template saved");
      setForm({ name: "", subject: "", html_body: "", class_id: "", category: "General" });
      load();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (id) => {
    await axios.delete(`${API}/admin/templates/${id}`, { headers: authHeader() });
    load();
  };

  const sendTest = async (id) => {
    const res = await axios.post(`${API}/admin/templates/${id}/test`, {}, { headers: authHeader() });
    toast[res.data.sent ? "success" : "error"](res.data.sent ? `Test sent to ${res.data.to}` : "Email not configured (add Resend key)");
  };

  return (
    <div data-testid="admin-templates" className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-2xl border border-[#E7E5E4] p-6">
        <h3 className="font-display text-lg font-medium text-[#1C1917] mb-4">New follow-up template</h3>
        <Label className="text-sm">Template name</Label>
        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="tpl-name" className="mt-1.5 mb-3 rounded-xl h-11" placeholder="Thanks for attending" />
        <Label className="text-sm">Subject</Label>
        <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
          data-testid="tpl-subject" className="mt-1.5 mb-3 rounded-xl h-11" placeholder="Great having you, {{first_name}}!" />
        <Label className="text-sm">Body (HTML — use {"{{first_name}}"} to personalize)</Label>
        <Textarea value={form.html_body} onChange={(e) => setForm({ ...form, html_body: e.target.value })}
          data-testid="tpl-body" className="mt-1.5 mb-3 rounded-xl min-h-[140px]"
          placeholder="<p>Hi {{first_name}}, thanks for joining today's class...</p>" />
        <Label className="text-sm">For class (optional)</Label>
        <Select value={form.class_id || "none"} onValueChange={(v) => setForm({ ...form, class_id: v === "none" ? "" : v })}>
          <SelectTrigger className="mt-1.5 mb-4 rounded-xl h-11" data-testid="tpl-class"><SelectValue placeholder="Any" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Any / general</SelectItem>
            {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Label className="text-sm">Category</Label>
        <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
          <SelectTrigger className="mt-1.5 mb-4 rounded-xl h-11" data-testid="tpl-category"><SelectValue /></SelectTrigger>
          <SelectContent>
            {TEMPLATE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={create} data-testid="tpl-save" className="rounded-xl bg-[#D97757] hover:bg-[#C26243] text-white gap-2">
          <Plus className="w-4 h-4" /> Save template
        </Button>
      </div>

      <div>
        <div className="flex items-center gap-3 mb-4">
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger className="w-60 rounded-xl h-10" data-testid="tpl-cat-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {TEMPLATE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-sm text-[#57534E]">{list.length} templates</span>
        </div>
        {list.length === 0 && <p className="text-[#57534E] text-sm">No templates yet.</p>}
        {TEMPLATE_CATEGORIES
          .filter((cat) => catFilter === "all" || catFilter === cat)
          .map((cat) => {
            const items = list.filter((t) => (t.category || "General") === cat);
            if (items.length === 0) return null;
            return (
              <div key={cat} className="mb-6" data-testid={`tpl-group-${cat}`}>
                <div className="text-xs tracking-[0.15em] uppercase font-bold text-[#D97757] mb-2">{cat}</div>
                <div className="space-y-3">
                  {items.map((t) => (
                    <div key={t.id} className="bg-white rounded-2xl border border-[#E7E5E4] p-5" data-testid={`tpl-item-${t.id}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="font-medium text-[#1C1917]">{t.name}</div>
                          <div className="text-sm text-[#57534E]">{t.subject}</div>
                        </div>
                        <div className="flex gap-2">
                          <Button onClick={() => sendTest(t.id)} variant="outline" size="sm" className="rounded-lg gap-1.5">
                            <Send className="w-3.5 h-3.5" /> Test
                          </Button>
                          <Button onClick={() => remove(t.id)} variant="outline" size="sm" className="rounded-lg text-red-600">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

// ---------- Campaigns ----------
const Campaigns = ({ classes }) => {
  const [list, setList] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({ name: "", class_id: "buyers-roadmap", template_id: "", trigger: "manual", hours_after_end: 24 });

  const load = async () => {
    try {
      setList((await axios.get(`${API}/admin/campaigns`, { headers: authHeader() })).data);
      setTemplates((await axios.get(`${API}/admin/templates`, { headers: authHeader() })).data);
    } catch { toast.error("Failed to load campaigns"); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name || !form.template_id) { toast.error("Name and template required"); return; }
    try { await axios.post(`${API}/admin/campaigns`, form, { headers: authHeader() }); toast.success("Campaign created"); load(); }
    catch { toast.error("Create failed"); }
  };
  const sendNow = async (id) => {
    const res = await axios.post(`${API}/admin/campaigns/${id}/send`, {}, { headers: authHeader() });
    toast.success(`Sent ${res.data.sent} of ${res.data.total_contacts}`);
    load();
  };
  const remove = async (id) => { await axios.delete(`${API}/admin/campaigns/${id}`, { headers: authHeader() }); load(); };

  return (
    <div data-testid="admin-campaigns" className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-2xl border border-[#E7E5E4] p-6">
        <h3 className="font-display text-lg font-medium text-[#1C1917] mb-4">New campaign</h3>
        <Label className="text-sm">Campaign name</Label>
        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="camp-name" className="mt-1.5 mb-3 rounded-xl h-11" placeholder="Tuesday class follow-up" />
        <Label className="text-sm">Target class</Label>
        <Select value={form.class_id} onValueChange={(v) => setForm({ ...form, class_id: v })}>
          <SelectTrigger className="mt-1.5 mb-3 rounded-xl h-11" data-testid="camp-class"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All classes</SelectItem>
            {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Label className="text-sm">Email template</Label>
        <Select value={form.template_id} onValueChange={(v) => setForm({ ...form, template_id: v })}>
          <SelectTrigger className="mt-1.5 mb-3 rounded-xl h-11" data-testid="camp-template"><SelectValue placeholder="Choose template" /></SelectTrigger>
          <SelectContent>
            {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Label className="text-sm">Sending</Label>
        <Select value={form.trigger} onValueChange={(v) => setForm({ ...form, trigger: v })}>
          <SelectTrigger className="mt-1.5 mb-3 rounded-xl h-11" data-testid="camp-trigger"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="manual">Manual (I click send)</SelectItem>
            <SelectItem value="auto">Auto — after each class ends</SelectItem>
          </SelectContent>
        </Select>
        {form.trigger === "auto" && (
          <>
            <Label className="text-sm">Hours after class ends</Label>
            <Input type="number" value={form.hours_after_end}
              onChange={(e) => setForm({ ...form, hours_after_end: parseInt(e.target.value || "0", 10) })}
              data-testid="camp-hours" className="mt-1.5 mb-3 rounded-xl h-11" />
          </>
        )}
        <Button onClick={create} data-testid="camp-save" className="rounded-xl bg-[#D97757] hover:bg-[#C26243] text-white gap-2 mt-1">
          <Plus className="w-4 h-4" /> Create campaign
        </Button>
      </div>

      <div className="space-y-3">
        {list.length === 0 && <p className="text-[#57534E] text-sm">No campaigns yet.</p>}
        {list.map((c) => (
          <div key={c.id} className="bg-white rounded-2xl border border-[#E7E5E4] p-5" data-testid={`camp-item-${c.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-medium text-[#1C1917]">{c.name}</div>
                <div className="text-sm text-[#57534E]">
                  {c.trigger === "auto" ? `Auto · ${c.hours_after_end}h after class` : "Manual"} · sent {c.total_sent || 0}
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => sendNow(c.id)} variant="outline" size="sm" className="rounded-lg gap-1.5">
                  <Send className="w-3.5 h-3.5" /> Send now
                </Button>
                <Button onClick={() => remove(c.id)} variant="outline" size="sm" className="rounded-lg text-red-600">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- Pipeline (drag & drop) ----------
const STAGE_COLORS = {
  "signed-up": "#A8A29E", "attended": "#D97757", "initial-call": "#C2884A",
  "pre-qualified": "#9A8C5A", "pre-approved": "#6B8E6B", "under-contract": "#5B8AA6", "funded": "#2E7D5B",
};

const ContactCard = ({ c, onOpen, onDragStart }) => (
  <div
    draggable
    onDragStart={(e) => onDragStart(e, c.id)}
    onClick={() => onOpen(c)}
    data-testid={`pipeline-card-${c.id}`}
    className="bg-white rounded-xl border border-[#E7E5E4] p-3 mb-2 cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all"
  >
    <div className="font-medium text-[#1C1917] text-sm">{c.first_name} {c.last_name}</div>
    <div className="text-xs text-[#57534E] truncate">{c.email}</div>
    <div className="text-[11px] text-[#A8A29E] mt-1">{c.class_label || c.class_id}</div>
    {c.notes ? <div className="text-[11px] text-[#C2884A] mt-1 flex items-center gap-1"><StickyNote className="w-3 h-3" /> note</div> : null}
  </div>
);

const Pipeline = ({ stages }) => {
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setContacts((await axios.get(`${API}/admin/contacts`, { headers: authHeader() })).data); }
    catch { toast.error("Failed to load pipeline"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onDragStart = (e, id) => e.dataTransfer.setData("id", id);

  const moveTo = async (id, stage) => {
    setContacts((prev) => prev.map((c) => (c.id === id ? { ...c, stage } : c)));
    try {
      const res = await axios.patch(`${API}/admin/contacts/${id}`, { stage }, { headers: authHeader() });
      if (res.data.auto_email_sent) toast.success("Moved · auto follow-up email sent");
      else toast.success("Moved");
    } catch { toast.error("Move failed"); load(); }
  };

  const onDrop = (e, stage) => { e.preventDefault(); const id = e.dataTransfer.getData("id"); if (id) moveTo(id, stage); };

  const openContact = (c) => { setSelected(c); setNotes(c.notes || ""); };
  const saveNotes = async () => {
    await axios.patch(`${API}/admin/contacts/${selected.id}`, { notes }, { headers: authHeader() });
    setContacts((prev) => prev.map((c) => (c.id === selected.id ? { ...c, notes } : c)));
    setSelected((s) => ({ ...s, notes }));
    toast.success("Notes saved");
  };
  const sendStageEmail = async () => {
    const res = await axios.post(`${API}/admin/contacts/${selected.id}/send-stage-email`, {}, { headers: authHeader() });
    toast[res.data.sent ? "success" : "error"](res.data.sent ? "Stage email sent" : "No enabled workflow email for this stage");
  };

  const byStage = (sid) => contacts.filter((c) => (c.stage || "signed-up") === sid);

  return (
    <div data-testid="admin-pipeline">
      <div className="flex items-center gap-3 mb-4">
        <Button onClick={load} variant="outline" className="rounded-xl h-9 gap-2" data-testid="pipeline-refresh">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
        <span className="text-sm text-[#57534E]">{contacts.length} contacts · drag cards between stages</span>
      </div>

      <div className="flex gap-4 overflow-x-auto pb-4">
        {stages.map((s) => (
          <div
            key={s.id}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => onDrop(e, s.id)}
            data-testid={`pipeline-col-${s.id}`}
            className="min-w-[240px] w-[240px] shrink-0 bg-[#F4EBE1]/60 rounded-2xl p-3"
          >
            <div className="flex items-center justify-between mb-3 px-1">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: STAGE_COLORS[s.id] || "#A8A29E" }} />
                <span className="font-semibold text-sm text-[#1C1917]">{s.label}</span>
              </div>
              <span className="text-xs text-[#78716C] bg-white rounded-full px-2 py-0.5">{byStage(s.id).length}</span>
            </div>
            {loading ? <div className="text-xs text-[#A8A29E] px-1">Loading…</div> :
              byStage(s.id).map((c) => <ContactCard key={c.id} c={c} onOpen={openContact} onDragStart={onDragStart} />)}
          </div>
        ))}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="rounded-2xl">
          {selected && (
            <div data-testid="pipeline-contact-dialog">
              <DialogTitle className="font-display text-xl font-medium text-[#1C1917]">{selected.first_name} {selected.last_name}</DialogTitle>
              <p className="text-sm text-[#57534E]">{selected.email} · {selected.phone || "no phone"}</p>
              <p className="text-sm text-[#57534E] mt-1">{selected.class_label} · role: {selected.role || "—"}</p>
              <p className="text-sm text-[#57534E]">Referred by: {selected.referred_by || "—"}</p>
              <div className="mt-4">
                <Label className="text-sm">Notes (shared)</Label>
                <Textarea data-testid="contact-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
                  className="mt-1.5 rounded-xl min-h-[100px]" placeholder="e.g., prefers evening calls, referred by Jane…" />
              </div>
              <div className="flex gap-2 mt-4">
                <Button onClick={saveNotes} data-testid="contact-save-notes" className="rounded-xl bg-[#D97757] hover:bg-[#C26243] text-white gap-2">
                  Save notes
                </Button>
                <Button onClick={sendStageEmail} variant="outline" data-testid="contact-send-stage-email" className="rounded-xl gap-2">
                  <Send className="w-4 h-4" /> Send stage email
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------- Workflows ----------
const Workflows = () => {
  const [rows, setRows] = useState([]);
  const [templates, setTemplates] = useState([]);

  const load = async () => {
    try {
      setRows((await axios.get(`${API}/admin/workflows`, { headers: authHeader() })).data);
      setTemplates((await axios.get(`${API}/admin/templates`, { headers: authHeader() })).data);
    } catch { toast.error("Failed to load workflows"); }
  };
  useEffect(() => { load(); }, []);

  const save = async (row) => {
    await axios.put(`${API}/admin/workflows/${row.stage_id}`,
      { template_id: row.template_id || "", auto: row.auto, enabled: row.enabled }, { headers: authHeader() });
    toast.success(`${row.stage_label} workflow saved`);
  };
  const patch = (sid, key, val) => setRows((prev) => prev.map((r) => (r.stage_id === sid ? { ...r, [key]: val } : r)));

  return (
    <div data-testid="admin-workflows">
      <p className="text-sm text-[#57534E] mb-5">
        Assign a follow-up email to each pipeline stage. Turn on <b>Auto-send</b> to email the contact
        the moment they move into that stage, or leave it off and send manually from a contact card.
        Create the emails in the <b>Templates</b> tab first.
      </p>
      <div className="space-y-3">
        {rows.map((r) => (
          <div key={r.stage_id} data-testid={`workflow-${r.stage_id}`}
            className="bg-white rounded-2xl border border-[#E7E5E4] p-5 flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex items-center gap-2 md:w-56">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: STAGE_COLORS[r.stage_id] || "#A8A29E" }} />
              <span className="font-semibold text-[#1C1917]">{r.stage_label}</span>
            </div>
            <div className="flex-1">
              <Select value={r.template_id || "none"} onValueChange={(v) => patch(r.stage_id, "template_id", v === "none" ? "" : v)}>
                <SelectTrigger data-testid={`workflow-template-${r.stage_id}`} className="rounded-xl h-10">
                  <SelectValue placeholder="No email" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No email</SelectItem>
                  {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-[#D97757]" />
              <span className="text-sm text-[#57534E]">Auto-send</span>
              <Switch checked={r.auto} onCheckedChange={(v) => patch(r.stage_id, "auto", v)} data-testid={`workflow-auto-${r.stage_id}`} />
            </div>
            <Button onClick={() => save(r)} data-testid={`workflow-save-${r.stage_id}`}
              className="rounded-xl bg-[#1C1917] hover:bg-black text-white h-10">Save</Button>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- Dashboard ----------
export default function Admin() {
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [classes, setClasses] = useState([]);
  const [stages, setStages] = useState([]);

  useEffect(() => {
    if (!authed) return;
    axios.get(`${API}/admin/me`, { headers: authHeader() }).catch(() => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); });
    axios.get(`${API}/classes`).then((r) => setClasses(r.data)).catch(() => {});
    axios.get(`${API}/admin/stages`, { headers: authHeader() }).then((r) => setStages(r.data)).catch(() => {});
  }, [authed]);

  if (!authed) return <Login onLogin={() => setAuthed(true)} />;

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); };

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <header className="bg-white border-b border-[#E7E5E4] px-6 md:px-12 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-xl bg-[#D97757] flex items-center justify-center">
            <GraduationCap className="w-5 h-5 text-white" />
          </span>
          <span className="font-display font-semibold text-[#1C1917]">Admin CRM</span>
        </div>
        <Button onClick={logout} variant="outline" className="rounded-xl gap-2" data-testid="admin-logout">
          <LogOut className="w-4 h-4" /> Sign out
        </Button>
      </header>

      <main className="px-6 md:px-12 py-8 max-w-7xl mx-auto">
        <Tabs defaultValue="pipeline">
          <TabsList className="mb-6 flex-wrap h-auto">
            <TabsTrigger value="pipeline" data-testid="tab-pipeline" className="gap-2"><KanbanSquare className="w-4 h-4" /> Pipeline</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts" className="gap-2"><Users className="w-4 h-4" /> Contacts</TabsTrigger>
            <TabsTrigger value="workflows" data-testid="tab-workflows" className="gap-2"><Workflow className="w-4 h-4" /> Workflows</TabsTrigger>
            <TabsTrigger value="templates" data-testid="tab-templates" className="gap-2"><Mail className="w-4 h-4" /> Templates</TabsTrigger>
            <TabsTrigger value="campaigns" data-testid="tab-campaigns" className="gap-2"><Send className="w-4 h-4" /> Campaigns</TabsTrigger>
          </TabsList>
          <TabsContent value="pipeline"><Pipeline stages={stages} /></TabsContent>
          <TabsContent value="contacts"><Contacts classes={classes} /></TabsContent>
          <TabsContent value="workflows"><Workflows /></TabsContent>
          <TabsContent value="templates"><Templates classes={classes} /></TabsContent>
          <TabsContent value="campaigns"><Campaigns classes={classes} /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
